import React, { useState, useEffect } from "react";
import axios from "axios";
import DesignWorkflow from "./DesignWorkflow";
import WorkflowNode from "./WorkflowNode";
import ViewWorkflow from "./ViewWorkflow";
import AdminOnboardingInitiate from "./AdminOnboardingInitiate"; // <--- NEW IMPORT ADDED
import Approval from "./Approval";
import VendorStatus from "./VendorStatus";
import BASE_URL from "./URL";
import Swal from "sweetalert2";


// Trash icon component
const Trash = ({ size = 18, className = "", onClick, title }) => (
  <svg onClick={onClick} xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className={className}
    title={title} style={{ cursor: 'pointer' }}>
    <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" x2="10" y1="11" y2="17" />
    <line x1="14" x2="14" y1="11" y2="17" />
  </svg>
);

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("approval"); // Default to approval
  const [notesOpen, setNotesOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [companyLogo, setCompanyLogo] = useState(null);
  const [userLogo, setUserLogo] = useState('Main.png'); // Default logo
  const [tabPerms, setTabPerms] = useState({ can_design: false, can_view: false, can_initiate: false, can_approval: true, can_status: false });
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('access'));   // <-- ADD THIS
  const inactivityTimeoutRef = React.useRef(null); // For inactivity timeout

  const api = axios.create({ baseURL: 'https://django-backend-115705416374.asia-south1.run.app' });
  // const api = axios.create({ baseURL: 'http://localhost:8000'});
  api.interceptors.request.use(config => {
    const token = localStorage.getItem('access');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  api.interceptors.response.use(
    res => res,
    async (error) => {
      const originalRequest = error.config;

      // Check if session was terminated by scheduler
      if (error.response?.status === 401) {
        const errorCode = error.response?.data?.code || error.response?.data?.detail?.code;
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || '';

        // If session was terminated (by scheduler), immediately logout
        if (errorCode === 'session_terminated' || errorMessage.includes('session has been terminated')) {
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          localStorage.removeItem('session_id');
          localStorage.removeItem('landing');
          localStorage.removeItem('email_b64');
          // Redirect to login immediately
          window.location.href = '/';
          return Promise.reject(error);
        }

        // Try to refresh token for other 401 errors
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const refresh = localStorage.getItem('refresh');
          if (refresh) {
            try {
              const res = await api.post('/api/token/refresh/', { refresh });
              localStorage.setItem('access', res.data.access);
              originalRequest.headers.Authorization = `Bearer ${res.data.access}`;
              return api(originalRequest);
            } catch {
              localStorage.removeItem('access');
              localStorage.removeItem('refresh');
              localStorage.removeItem('session_id');
              localStorage.removeItem('landing');
              localStorage.removeItem('email_b64');
              window.location.href = '/';
            }
          } else {
            // No refresh token - redirect to login
            localStorage.removeItem('access');
            localStorage.removeItem('refresh');
            localStorage.removeItem('session_id');
            localStorage.removeItem('landing');
            localStorage.removeItem('email_b64');
            window.location.href = '/';
          }
        }
      }
      return Promise.reject(error);
    }
  );

  // Logout function
  const handleLogout = async () => {
    try {
      await api.post('/api/logout/');
    } catch (err) {
      // Logout API failed
    }
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('session_id');
    localStorage.removeItem('landing');
    localStorage.removeItem('email_b64');
    // Redirect to login page explicitly
    window.location.href = '/';
  };

  const handleLogoClick = () => {
    setActiveTab('approval');
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/api/notifications/');
      const allNotifications = res.data || [];
      // Filter out read notifications to only show unread ones
      const unreadNotifications = allNotifications.filter(n => !n.read);
      setNotifications(unreadNotifications);
    } catch (e) {
      // ignore
    }
  };

  const fetchUserEmail = async () => {
    try {
      const res = await api.get('/api/user-profile/');
      const b64 = res.data?.email_b64 || '';
      let decoded = '';
      if (b64) {
        try { decoded = atob(b64); } catch { decoded = ''; }
      }
      if (decoded) {
        // persist encoded email so we can reuse without another API call
        try { localStorage.setItem('email_b64', b64); } catch { }
        setUserEmail(decoded);
      } else {
        setUserEmail('');
      }
    } catch (e) {
      // Fallbacks: encoded email in localStorage → plain saved → JWT
      const storedB64 = localStorage.getItem('email_b64');
      if (storedB64) {
        try { setUserEmail(atob(storedB64)); return; } catch { }
      }

      const lsEmail =
        localStorage.getItem('email') ||
        localStorage.getItem('user_email') ||
        localStorage.getItem('google_email') ||
        localStorage.getItem('login_email') ||
        '';

      if (lsEmail) {
        setUserEmail(lsEmail);
        return;
      }

      const token = localStorage.getItem('access');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUserEmail(payload.email || payload.username || '');
        } catch (err) {
          setUserEmail('');
        }
      } else {
        setUserEmail('');
      }
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        // Check if user came from STL Employee login and set Approval tab as default
        const landing = localStorage.getItem('landing');
        if (landing === 'admin') {
          setActiveTab('approval');
        }

        // Initialize all data in parallel for better performance
        const promises = [
          fetchNotifications(),
          fetchUserEmail(),
          // Fetch user logo
          api.get('/api/user-logo/').then(res => {
            const logoName = res.data?.logo || 'STL_Logo.png';
            setUserLogo(logoName);
          }).catch(() => {
            setUserLogo('STL_Logo.png');
          }),
          // Fetch company logo
          api.get('/api/company-logo/?company=STL-DIGITAL').then(res => {
            const logoUrl = res.data?.logo || null;
            setCompanyLogo(logoUrl);
          }).catch(() => {
            setCompanyLogo(null);
          }),
          // Fetch tab permissions
          api.get('/api/user-permissions/').then(res => {
            setTabPerms(res.data || { can_approval: true });
          }).catch(() => {
            setTabPerms({ can_approval: true }); // Ensure approval is always available
          })
        ];

        await Promise.allSettled(promises);

      } catch (error) {
        setHasError(true);
        // Fallback to basic functionality
        setTabPerms({ can_approval: true });
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const toggleNotes = async () => {
    const newOpen = !notesOpen;
    setNotesOpen(newOpen);
    if (newOpen) {
      // Fetch notifications first
      try {
        const res = await api.get('/api/notifications/');
        const allNotifications = res.data || [];
        // Mark unread notifications as read when opening
        const unreadIds = allNotifications.filter(n => !n.read).map(n => n.id);
        if (unreadIds.length > 0) {
          try { await api.post('/api/notifications/read/', { ids: unreadIds }); } catch { }
        }
        // Filter out read notifications to only show unread ones
        const unreadNotifications = allNotifications.filter(n => !n.read);
        setNotifications(unreadNotifications);
      } catch (e) {
        // ignore
      }
    }
  };

  const toggleProfile = () => {
    setProfileOpen(!profileOpen);
    setNotesOpen(false); // Close notifications when opening profile
  };

  const getInitials = (raw) => {
    const email = (raw || '').trim();
    if (!email.includes('@')) {
      // If it's not an email, derive from string safely
      const s = email || 'AU';
      return s.slice(0, 2).toUpperCase();
    }

    const local = email.split('@')[0] || '';
    if (!local) return 'AU';

    const parts = local.split('.').filter(Boolean);
    if (parts.length >= 2) {
      return ((parts[0][0] || '') + (parts[1][0] || '')).toUpperCase();
    }

    // If only one part, use first and next alpha char if available
    const letters = local.replace(/[^a-z]/gi, '');
    if (letters.length >= 2) return letters.slice(0, 2).toUpperCase();
    if (letters.length === 1) return letters[0].toUpperCase();
    return 'AU';
  };

  const getTokenEmail = () => {
    const token = localStorage.getItem('access');
    if (!token) return '';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.email || payload.username || '';
    } catch {
      return '';
    }
  };

  const getStoredEmail = () => (
    localStorage.getItem('email') ||
    localStorage.getItem('user_email') ||
    localStorage.getItem('google_email') ||
    localStorage.getItem('login_email') ||
    ''
  );

  // Remove read notifications from the dropdown when user opens it
  useEffect(() => {
    if (!notesOpen) return;
    // Filter out all read notifications (not just task notifications)
    const cleaned = notifications.filter(n => !n.read);
    if (cleaned.length !== notifications.length) setNotifications(cleaned);
  }, [notesOpen, notifications]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileOpen && !event.target.closest('.profile-dropdown')) {
        setProfileOpen(false);
      }
      if (notesOpen && !event.target.closest('.notification-bell')) {
        setNotesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen, notesOpen]);


  // ---------------- Idle Session Logout (15 min) ----------------
  useEffect(() => {
    if (!token) return;

    let timer;
    let sessionCheckInterval;

    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        try {
          await api.post('/api/logout/', { logout_reason: 'auto' });  // Notify backend to invalidate session (auto logout)
        } catch (err) {
          // Ignore logout error
        }

        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('session_id');
        localStorage.removeItem('landing');
        localStorage.removeItem('email_b64');

        setToken(null);

        Swal.fire({
          icon: 'info',
          title: 'Session Timeout',
          text: 'You have been logged out due to inactivity.',
          timer: 3000,
          showConfirmButton: false
        });

        window.location.href = '/';
      }, 900000);  // 900,000 ms = 15 minutes
    };

    // Check session validity periodically (every 30 seconds)
    const checkSession = async () => {
      try {
        const sessionRes = await api.get('/api/check-session/');
        if (!sessionRes.data?.valid) {
          // Session invalid - redirect to login
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          localStorage.removeItem('session_id');
          localStorage.removeItem('landing');
          localStorage.removeItem('email_b64');
          setToken(null);
          Swal.fire({
            icon: 'info',
            title: 'Session Expired',
            text: 'Your session has been terminated. Please login again.',
            timer: 3000,
            showConfirmButton: false
          });
          window.location.href = '/';
        }
      } catch (err) {
        if (err.response?.status === 401) {
          // Session expired - redirect to login
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          localStorage.removeItem('session_id');
          localStorage.removeItem('landing');
          localStorage.removeItem('email_b64');
          setToken(null);
          window.location.href = '/';
        }
      }
    };

    const events = [
      'mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'
    ];

    events.forEach(event =>
      window.addEventListener(event, resetTimer)
    );

    resetTimer(); // Initialize timer on load
    sessionCheckInterval = setInterval(checkSession, 30000); // Check session every 30 seconds

    return () => {
      clearTimeout(timer);
      if (sessionCheckInterval) clearInterval(sessionCheckInterval);
      events.forEach(event =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [token, api]);

  // 🆕 Scroll to top when switching tabs
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);


  // Show loading state
  if (isLoading) {
    return (
      <div className="admin-page">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#666', fontSize: '16px' }}>Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  // Show error state with retry option
  if (hasError) {
    return (
      <div className="admin-page">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <h2 style={{ color: '#dc2626' }}>Failed to Load Admin Panel</h2>
          <p style={{ color: '#666' }}>Please check your connection and try again.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* ---------- Navigation Bar ---------- */}
      <nav className="admin-navbar">
        <div className="navbar-left">

          <img draggable={false}
            src={`/${userLogo}`}
            alt="Company Logo"
            className="company-logo"
            style={{ maxHeight: 48, maxWidth: 160, cursor: 'pointer' }}
            onClick={handleLogoClick}
          />


          <ul className="nav-links">
            {/* 1) View Workflow */}
            {/* {tabPerms.can_view && (
              <li
                className={activeTab === "view" ? "active" : ""}
                onClick={() => setActiveTab("view")}
              >
                View Workflow
              </li>
            )} */}

            {/* 2) Approval - Available to all users */}
            <li
              className={activeTab === "approval" ? "active" : ""}
              onClick={() => setActiveTab("approval")}
            >
              Approval
            </li>

            {/* 3) Design Workflow */}
            {tabPerms.can_design && (
              <li
                className={activeTab === "design" ? "active" : ""}
                onClick={() => setActiveTab("design")}
              >
                Design Workflow
              </li>
            )}

            {/* 4) Registration Initiation */}
            {tabPerms.can_initiate && (
              <li
                className={activeTab === "initiate" ? "active" : ""}
                onClick={() => setActiveTab("initiate")}
              >
                Registration Initiation
              </li>
            )}

            {/* 5) Vendor Status */}
            {tabPerms.can_status && (
              <li
                className={activeTab === "status" ? "active" : ""}
                onClick={() => setActiveTab("status")}
              >
                Vendor Status
              </li>
            )}
          </ul>
        </div>
        <div className="navbar-right">
          <div className="notification-bell" onClick={toggleNotes} title="Notifications">
            <span className="bell-icon">🔔</span>
            {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
          </div>
          {notesOpen && (
            <div className="notifications-dropdown">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 'bold' }}>Notifications</div>
                {notifications.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: '#f3f4f6',
                      color: '#ef4444',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fee2e2';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const notificationIds = notifications.map(n => n.id);
                        if (notificationIds.length > 0) {
                          await api.post('/api/notifications/read/', { ids: notificationIds });
                        }
                        setNotifications([]);
                        // Don't refetch immediately to prevent reloading read notifications
                        // They will refresh when dropdown is reopened or new notifications arrive
                      } catch (err) {
                        // Failed to clear notifications
                        // Still clear the local state even if API call fails
                        setNotifications([]);
                      }
                    }}
                    title="Clear all notifications"
                  >
                    <Trash size={16} />
                  </div>
                )}
              </div>
              {notifications.length === 0 && <div className="note-empty">No notifications</div>}
              {notifications.map(n => (
                <div key={n.id} className={`note-item ${n.read ? 'read' : 'unread'}`}>
                  <div className="note-title">{n.title}</div>
                  <div className="note-message">{n.message}</div>
                </div>
              ))}
            </div>
          )}

          <div className="profile-dropdown">
            <button className="profile-btn" onClick={toggleProfile} title="Profile">
              <div className="profile-avatar">
                {getInitials(userEmail || getStoredEmail() || getTokenEmail())}
              </div>
              <span className="profile-email">{userEmail || getStoredEmail() || getTokenEmail()}</span>
            </button>
            {profileOpen && (
              <div className="profile-dropdown-menu show">
                <div className="profile-dropdown-item" onClick={handleLogout}>
                  <span>🚪</span>
                  <span>Logout</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ---------- Main Content ---------- */}
      {/* The components now render directly into <main> without an extra wrapper/container. */}
      <main>
        {activeTab === "design" && (
          // <section className="tab-panel"> <-- REMOVED CONTAINER
          <WorkflowNode />
          // </section>
        )}

        {/* {activeTab === "view" && (
          // <section className="tab-panel"> <-- REMOVED CONTAINER
            <ViewWorkflow />
          // </section>
        )} */}

        {activeTab === "initiate" && (
          // <section className="tab-panel"> <-- REMOVED CONTAINER
          <AdminOnboardingInitiate />
          // </section>
        )}

        {/* Vendor Status Component (NEW) */}
        {activeTab === "status" && (
          <VendorStatus />
        )}

        {/* Approval - Available to all users */}
        {activeTab === "approval" && (
          <Approval />
        )}
      </main>
    </div>
  );
};

export default AdminPage;